import LabeledSelect from 'components/LabeledSelect';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
} from 'services/devices/devices.api';
import { buildDevicesBySystemAndTypeMap, buildDeviceValueMap } from '../utils/globalActionsUtils';
import DeviceCustomInput from './DeviceCustomInput';

const Device = ({ setData, data, setError }) => {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();
  const devicesBySystemAndTypeMap = buildDevicesBySystemAndTypeMap(connectedDevices);

  // data.[0].depth1 accesses the first data point's depth1 value for Aware Flood devices
  // this exists for a universally accessible metadata map for devices in Global Actions
  const dataMaps = {
    flood: {
      stage: {
        'data.depth1': { name: 'Sensor Depth', unitName: 'Inches', units: 'Inches', type: 'float' },
        'data.sensordepths': {
          name: 'Actual Depths',
          unitName: 'Inches',
          units: 'Inches',
          type: 'float',
        },
        'data.depthDet1': {
          name: 'Depth Threshold 1',
          unitName: 'Inches',
          units: 'Inches',
          type: 'float',
        },
        'data.depthDet2': {
          name: 'Depth Threshold 2',
          unitName: 'Inches',
          units: 'Inches',
          type: 'float',
        },
        'data.depthDet3': {
          name: 'Depth Threshold 3',
          unitName: 'Inches',
          units: 'Inches',
          type: 'float',
        },
        'data.airTemp': {
          name: 'Air Temperature',
          unitName: '°C',
          units: 'Celsius',
          type: 'float',
        },
        'data.baro': { name: 'Pressure', unitName: 'mBar', units: 'mBar', type: 'float' },
        'data.battery': { name: 'Battery', unitName: 'Voltage', units: 'Voltage', type: 'float' },
        'data.depthInd': {
          name: 'Depth Detection Indicator (Threshold Met)',
          units: 'flag',
          type: 'bool',
        },
        'data.drInd': {
          name: 'Drop Rate Indicator (Threshold Met)',
          units: 'flag',
          type: 'bool',
        },
        'data.dropSDI': {
          name: 'Device Drop Count (SDI-12) Tipping bucket',
          unitName: 'Drops',
          units: 'Drops', // number of drops
          type: 'float',
        },
        'data.soilSDI': {
          name: 'Soil Moisture Reading (SDI-12)',
          unitName: 'ADC (Analog to Digital Converter)',
          units: 'ADC',
          type: 'float',
        },
        'data.soilTempSDI': {
          name: 'Soil Temperature Reading (SDI-12)',
          unitName: '°C',
          units: 'Celsius',
          type: 'float',
        },
        'data.ffi1': { name: 'Flash Flood Indicator', units: 'flag', type: 'bool' },
        'data.h2oTemp': {
          name: 'Water Temperature',
          unitName: '°C',
          units: 'Celsius',
          type: 'float',
        },
        'data.rssi': { name: 'Signal Strength', unitName: 'dB', units: 'dB', type: 'float' },
      },
    },
    'rescalert devices': {
      'manual road closure': {
        'data.status': {
          name: 'Device Status',
          type: 'bool',
          units: 'flag',
          values: { true: 'Closed', false: 'Open' },
        },
      },
      'technology bridge_flashing lights': {
        'data.status': {
          name: 'Device Status',
          type: 'bool',
          units: 'flag',
          values: { true: 'Closed', false: 'Open' },
        },
      },
      'technology bridge_siren/giant voice': {
        'data.status': {
          name: 'Device Status',
          type: 'bool',
          units: 'flag',
          values: { true: 'Alerting', false: 'Idle' },
        },
      },
    },
    usgs: {
      'monitoring station': {
        //Generated inside of buildDeviceValueMap
      },
    },
  };

  const [systemType, setSystemType] = useState();
  const [deviceType, setDeviceType] = useState();
  const [deviceSubType, setDeviceSubType] = useState();

  useEffect(() => {
    // Null/undefined safe initialization: treat null and undefined the same
    if (
      data == null ||
      data?.node_id == null ||
      data?.deviceType == null ||
      data?.keyToCheck == null ||
      data?.keyToCompare == null ||
      data?.operator == null ||
      data?.valueToCompare == null ||
      data?.selectedDevice == null ||
      data?.inputValueType == null
    ) {
      setData({
        node_id: '',
        deviceType: '',
        keyToCheck: '',
        keyToCompare: '',
        operator: '',
        valueToCompare: '',
        selectedDevice: '',
        inputValueType: false,
        error: true,
      });
    }

    // Initialize local state from existing data.deviceType
    if (data.deviceType) {
      if (data.deviceType.includes('RescAlert Devices')) {
        setSystemType('RescAlert Devices');
        const types = data.deviceType.replace('RescAlert Devices ', '');
        setDeviceType(types);
        setDeviceSubType(types[1]);
      } else {
        const systemType = data.deviceType.split(' ')[0];
        const deviceType = data.deviceType.split(' ')[1].split('_')[0];
        const subType = data.deviceType.split(' ')[1].split('_')[1];
        setSystemType(systemType);
        setDeviceType(deviceType);
        setDeviceSubType(subType);
      }
    }
  }, []);

  const updateData = (item) => {
    if (item.selectedDevice && item.selectedDevice.data) {
      item.selectedDevice.data = [item.selectedDevice.data[0]];
    }
    setData({
      ...data,
      ...item,
      error: verifyData({ ...data, ...item }),
    });
  };

  const verifyData = (obj) => {
    let error = false;
    if (
      obj.node_id === '' ||
      obj.deviceType === '' ||
      obj.keyToCheck === '' ||
      (obj.keyToCompare === '' && obj.valueToCompare === '') ||
      (obj.keyToCompare === null && obj.valueToCompare === null) ||
      obj.operator === '' ||
      obj.operator === null
    ) {
      error = true;
      setError({ message: 'Action incomplete, check for missing fields.' });
    }
    if (!error) setError(null);
    return error;
  };

  const getDeviceTypeString = (device) => {
    return device?.system_type + ' ' + device?.device_type || '';
  };

  const operators = [
    { operator: '==', types: ['all'] },
    { operator: '!=', types: ['all'] },
    { operator: '>', types: ['float'] },
    { operator: '<', types: ['float'] },
    { operator: '>=', types: ['float'] },
    { operator: '<=', types: ['float'] },
  ];

  return (
    <div className="d-flex flex-column w-100">
      <div className="d-flex flex-column flex-md-row p-3 rounded-2 m-2 gap-3 bg-primary align-items-stretch align-items-md-center">
        <LabeledSelect
          label="System:"
          value={systemType || ''}
          onChange={(e) => {
            setSystemType(e.target.value);
            setDeviceType('');
            setDeviceSubType('');
            updateData({
              deviceType: '',
              // reset dependent fields
              node_id: null,
              selectedDevice: null,
              keyToCheck: null,
              valueToCompare: null,
              keyToCompare: null,
              operator: null,
            });
          }}
          className="flex-fill max-w-sm"
        >
          <option value="">-Select-</option>
          {Object.keys(deviceTypes || {}).map((type) => {
            if (!devicesBySystemAndTypeMap.has(type) || type === 'Exacq Vision') return null;
            return (
              <option key={type} value={type}>
                {type}
              </option>
            );
          })}
        </LabeledSelect>

        {systemType && (
          <LabeledSelect
            label="Device Type:"
            value={deviceType || ''}
            onChange={(e) => {
              setDeviceType(e.target.value);
              setDeviceSubType('');

              if (
                deviceTypes?.[systemType]?.[e.target.value] &&
                (Object.keys(deviceTypes[systemType][e.target.value] || {}).length === 0 ||
                  Object.keys(deviceTypes[systemType][e.target.value] || {}).includes(
                    'validDevices',
                  ))
              ) {
                updateData({
                  deviceType: systemType + ' ' + e.target.value,
                  // reset dependent fields
                  node_id: null,
                  selectedDevice: null,
                  keyToCheck: null,
                  valueToCompare: null,
                  keyToCompare: null,
                  operator: null,
                });
              } else {
                updateData({
                  deviceType: '',
                  // reset dependent fields
                  node_id: null,
                  selectedDevice: null,
                  keyToCheck: null,
                  valueToCompare: null,
                  keyToCompare: null,
                  operator: null,
                });
              }
            }}
            className="flex-fill max-w-sm"
          >
            <option value="">-Select-</option>
            {Object.keys(deviceTypes?.[systemType] || {}).map((type) => {
              const devices = devicesBySystemAndTypeMap.get(systemType).get(type) || [];
              if (devices.length === 0) return null;
              return (
                <option key={type} value={type}>
                  {type.replace('_', ' ')}
                </option>
              );
            })}
          </LabeledSelect>
        )}

        {deviceType &&
          Object.keys(deviceTypes?.[systemType]?.[deviceType] || {}).length > 0 &&
          !Object.keys(deviceTypes?.[systemType]?.[deviceType] || {}).includes('validDevices') && (
            <LabeledSelect
              label="Sub Type:"
              value={deviceSubType || ''}
              onChange={(e) => {
                setDeviceSubType(e.target.value);
                updateData({
                  deviceType: systemType + ' ' + deviceType + '_' + e.target.value,
                  // reset dependent fields
                  node_id: null,
                  selectedDevice: null,
                  keyToCheck: null,
                  valueToCompare: null,
                  keyToCompare: null,
                  operator: null,
                });
              }}
              className="flex-fill max-w-sm"
            >
              <option value="">-Select-</option>
              {Object.keys(deviceTypes?.[systemType]?.[deviceType] || {}).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </LabeledSelect>
          )}

        {data.deviceType && (
          <LabeledSelect
            label="Device Alias:"
            value={data.node_id || ''}
            onChange={(e) => {
              // find device by node_id
              const device = connectedDevices.find((d) => d.node_id.toString() === e.target.value);
              updateData({
                node_id: e.target.value,
                selectedDevice: device ? { ...device, data: [device.data[0]] } : null,
                // reset below
                keyToCheck: '',
                valueToCompare: '',
                keyToCompare: '',
                operator: '',
              });
            }}
            className="flex-fill max-w-sm"
          >
            <option value="">-Select-</option>
            {connectedDevices.map((device) => {
              if (getDeviceTypeString(device) === data.deviceType) {
                const label =
                  (device.device_type !== 'Manual Road Closure' ? device.static_id + ' ' : '') +
                  device.alias;
                return (
                  <option key={device.node_id} value={device.node_id}>
                    {label}
                  </option>
                );
              }
              return null;
            })}
          </LabeledSelect>
        )}
      </div>

      {/* Value / Operator / Compare block */}
      {data.selectedDevice && (
        <div className="d-flex p-3 rounded-2 m-2 mt-0 bg-primary w-auto h-auto flex-column flex-md-row gap-3 align-items-stretch align-items-md-center">
          <div className="fw-medium align-self-start">Value:</div>
          {/* Value */}
          <select
            className="bg-base p-1 w-100 w-md-50"
            onChange={(e) => {
              if (!e.target.value)
                updateData({ keyToCheck: e.target.value, operator: null, keyToCompare: '' });
              else updateData({ keyToCheck: e.target.value });
            }}
            value={data.keyToCheck || ''}
          >
            <option value="">-Select-</option>
            {(() => {
              const valueMap = buildDeviceValueMap(data.selectedDevice, dataMaps);
              return Object.keys(valueMap || {}).map((key) => (
                <option key={key} value={key}>
                  {valueMap[key].name}
                </option>
              ));
            })()}
          </select>

          {/* Operator */}
          {data.keyToCheck && (
            <select
              className="bg-base p-1"
              style={{ maxWidth: 75 }}
              onChange={(e) => updateData({ operator: e.target.value })}
              value={data.operator || ''}
            >
              <option value="">Select</option>
              {(() => {
                const valueMap = buildDeviceValueMap(data.selectedDevice, dataMaps);
                const typeOfKey = valueMap[data.keyToCheck]?.type;
                return operators.map((op) => {
                  if (op.types.includes('all') || op.types.includes(typeOfKey)) {
                    return (
                      <option key={op.operator} value={op.operator}>
                        {op.operator === '==' ? '=' : op.operator}
                      </option>
                    );
                  } else if (data.operator === op.operator) {
                    updateData({ operator: null });
                  }
                  return null;
                });
              })()}
            </select>
          )}

          {data.operator &&
            // Right side
            (data.inputValueType ? (
              // Custom Input
              <div className="flex-fill">
                <DeviceCustomInput
                  value={data.valueToCompare}
                  onChange={(value) => updateData({ valueToCompare: value })}
                  dataMapItem={buildDeviceValueMap(data.selectedDevice, dataMaps)[data.keyToCheck]}
                />
              </div>
            ) : (
              // Compare to another key
              <select
                className="bg-base p-1 w-100 w-md-50"
                onChange={(e) => updateData({ keyToCompare: e.target.value })}
                value={data.keyToCompare || ''}
              >
                <option value="">-Select-</option>
                {(() => {
                  const valueMap = buildDeviceValueMap(data.selectedDevice, dataMaps);
                  const keyType = valueMap[data.keyToCheck]?.units;
                  return Object.keys(valueMap || {}).map((key) => {
                    if (valueMap[key] && valueMap[key].units === keyType) {
                      return (
                        <option key={key} value={key}>
                          {valueMap[key].name}
                        </option>
                      );
                    }
                    return null;
                  });
                })()}
              </select>
            ))}

          {data.operator && (
            <Button
              variant="light"
              size="sm"
              className="rounded-2 text-nowrap w-auto"
              active={!!data.inputValueType}
              onClick={() =>
                updateData({
                  valueToCompare: '',
                  keyToCompare: '',
                  inputValueType: !data.inputValueType,
                })
              }
            >
              {data.inputValueType ? 'Change to compare' : 'Change to custom value'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Device;
